import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useQueryClient, useQuery } from "@tanstack/react-query";

interface ShieldCreditsState {
  credits: number;
  isPaidUser: boolean;
  isLoading: boolean;
  accountType: "individual" | "enterprise";
  refetch: () => void;
  deductCredit: (amount?: number) => Promise<boolean>;
}

const fetchProfileCredits = async (userId: string) => {
  const { data, error } = await supabase
    .from("profiles")
    .select("shield_credits, has_purchased_starter")
    .eq("user_id", userId)
    .single();

  if (error) throw error;
  return data;
};

export const useShieldCredits = (): ShieldCreditsState => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const prevCreditsRef = useRef<number | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["shield-credits", user?.id],
    queryFn: () => {
      if (!user) throw new Error("Sign in required");
      return fetchProfileCredits(user.id);
    },
    enabled: !!user,
    staleTime: 0,
  });

  // Fetch account_type with a separate query (column not in generated types yet)
  const { data: accountTypeData } = useQuery({
    queryKey: ["account-type", user?.id],
    queryFn: async () => {
      if (!user) throw new Error("Sign in required");
      const { data: row } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();
      return (row as any)?.account_type ?? "individual";
    },
    enabled: !!user,
    staleTime: 30000,
  });

  const credits = data?.shield_credits ?? 0;
  const isPaidUser = data?.has_purchased_starter ?? false;
  const accountType = (accountTypeData ?? "individual") as "individual" | "enterprise";

  // Show toast when credits increase (purchase fulfilled)
  useEffect(() => {
    if (data && prevCreditsRef.current !== null) {
      const newCredits = data.shield_credits;
      const oldCredits = prevCreditsRef.current;
      if (newCredits > oldCredits) {
        const added = newCredits - oldCredits;
        toast.success(`🛡️ +${added} credits added!`, {
          description: `Your balance is now ${newCredits} credits.`,
          duration: 6000,
        });
      }
    }
    if (data) {
      prevCreditsRef.current = data.shield_credits;
    }
  }, [data]);

  // Realtime listener for profile credit updates
  useEffect(() => {
    if (!user) return;

    // Unique name per hook instance: several components use this hook at once,
    // and reusing a subscribed channel name throws in newer client versions.
    const channel = supabase
      .channel(`profile-credits-${user.id}-${crypto.randomUUID()}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Invalidate query to force immediate re-fetch
          queryClient.invalidateQueries({ queryKey: ["shield-credits", user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const forceRefresh = useCallback(() => {
    if (user) {
      queryClient.invalidateQueries({ queryKey: ["shield-credits", user.id] });
    }
  }, [user, queryClient]);

  const deductCredit = useCallback(async (amount: number = 1): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc('deduct_shield_credit');

      if (error) {
        console.error("Error deducting credit:", error);
        forceRefresh();
        return false;
      }

      const result = data as { success: boolean; new_credits: number; is_lifetime?: boolean; error?: string };

      if (!result?.success) {
        console.error("Credit deduction failed:", result?.error);
        forceRefresh();
        return false;
      }

      // Optimistically update the cache immediately
      queryClient.setQueryData(["shield-credits", user.id], {
        shield_credits: result.new_credits,
        has_purchased_starter: isPaidUser,
      });
      prevCreditsRef.current = result.new_credits;

      // Post-action credit feedback
      toast(`${amount} credit${amount > 1 ? "s" : ""} used · ${result.new_credits} remaining`, {
        duration: 3000,
      });

      return true;
    } catch (err) {
      console.error("Error deducting credit:", err);
      forceRefresh();
      return false;
    }
  }, [user, forceRefresh, queryClient, isPaidUser]);

  return {
    credits,
    isPaidUser,
    isLoading,
    accountType,
    refetch: forceRefresh,
    deductCredit,
  };
};
